/* eslint-disable react/no-array-index-key */
import {
  Collapse,
  List,
  ListItem,
  InputAdornment,
  FormControl,
  Input,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import React, { Fragment } from "react";
import Search2LineIcon from "remixicon-react/Search2LineIcon";
import CircularProgress from "@mui/material/CircularProgress";

import { useQuery } from "react-query";
import { Text, Alert } from "$components";
import useStyles from "./SideMenu.styles";
import useAuth from "$hooks/useAuth";
import categoryApi from "$api/categoryApi";
import { formatCategory } from "$utils/categoryUtils";
import { CacheInvalidationAction } from "$utils/cache";
import { isReferencingCategoryName } from "$hooks/useCategoriesQuery";

// THIS is a patch to show the loader since there is no loader on categories
const useCategorie = (companyId, withReferencing) => {
  const token = localStorage.getItem("access_token");

  const { data, isLoading } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_CATEGORIES_BY_COMPANY_ID,
      companyId,
      withReferencing,
    ],
    queryFn: () => categoryApi.getCategory(token, companyId, withReferencing),
    enabled: !!companyId,
    cacheTime: 1000 * 60,
  }) as any;

  let categories = [];
  if (data?.data) {
    categories = formatCategory(data.data?.categories)?.groupedCategories || [];
  }

  return { categories, isLoading };
};

function SideMenu({
  companyId,
  filterSearch,
  setFilterSearch,
  filterByCategory,
  setFilterByCategory,
  withReferencing = true,
}: any) {
  const classes = useStyles();
  const [open, setOpen] = React.useState<any>([]);
  const { isLoading, categories } = useCategorie(companyId, withReferencing);
  const { isAdmin } = useAuth();

  const toggleOpened = (ref: string) => {
    const index = open.indexOf(ref);
    if (index > -1) {
      setOpen(open.filter((o: string) => o !== ref));
    } else {
      setOpen([...open, ref]);
    }
  };

  const onChangeFilter = (e: any) => {
    setFilterSearch(e.target.value);
  };

  const handleFilterCategory = (categoryID: string) => {
    // check/uncheck filter categ
    setFilterSearch("");
    setFilterByCategory(categoryID === filterByCategory ? null : categoryID);
  };

  if (!isLoading && categories?.length === 0) {
    return (
      <Alert severity="error" style={{ width: "100%" }}>
        {isAdmin()
          ? "Créez les catégories afin de pouvoir ajouter les produits au catalogue."
          : "Ce catalogue ne contient pas encore de catégories."}
      </Alert>
    );
  }

  return (
    <div className={classes.sideMenu}>
      <FormControl variant="outlined" className={classes.sidebarSearch}>
        <Input
          placeholder="Rechercher un produit"
          value={filterSearch}
          onChange={onChangeFilter}
          startAdornment={
            <InputAdornment position="start">
              <Search2LineIcon />
            </InputAdornment>
          }
        />
      </FormControl>
      <Text type="thead" className={classes.categorieLabel}>
        catégories
      </Text>
      {isLoading ? (
        <div className={classes.CircularProgress}>
          <CircularProgress color="inherit" style={{ width: 18, height: 18 }} />
        </div>
      ) : (
        <List className={classes.muiList}>
          {/* main categories */}
          {categories
            ?.filter(
              (category) =>
                withReferencing ||
                category.title
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "") !== "referencement"
            )
            .sort((a, b) => {
              const aIsReferencing = isReferencingCategoryName(a.title || "");
              const bIsReferencing = isReferencingCategoryName(b.title || "");

              if (aIsReferencing === bIsReferencing) return 0;
              if (aIsReferencing) return 1;
              return -1;
            })
            .map(({ id, title, subCategory = [] }, index) => (
              <Fragment key={`main_category_${index}`}>
                <div className={classes.categoryMain}>
                  <ListItem
                    button
                    onClick={() => handleFilterCategory(id)}
                    className={`${
                      filterByCategory === id ? classes.categActif : null
                    }`}
                  >
                    <Text
                      type="important"
                      className={`${classes.textMain} ${
                        open === title && classes.categActif
                      }`}
                    >
                      {title}
                    </Text>
                  </ListItem>
                  {subCategory.length > 0 && open.includes(title) && (
                    <ExpandLess onClick={() => toggleOpened(title)} />
                  )}
                  {subCategory.length > 0 && !open.includes(title) && (
                    <ExpandMore onClick={() => toggleOpened(title)} />
                  )}
                </div>
                {/* sub categories */}
                {subCategory.length > 0 && (
                  <Collapse
                    in={open.includes(title)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {subCategory.map((subCat: any) => (
                        <Fragment key={subCat.title}>
                          <ListItem
                            button
                            className={`${classes.textSub} ${
                              subCat.id === filterByCategory
                                ? classes.categActif
                                : null
                            }`}
                            onClick={() => handleFilterCategory(subCat.id)}
                          >
                            {subCat.title}
                          </ListItem>
                          {subCat.subCategory?.length > 0 && (
                            <Collapse in timeout="auto" unmountOnExit>
                              <ul className={classes.productUl}>
                                {subCat.subCategory?.map((child: any) => (
                                  <ListItem
                                    component="li"
                                    button
                                    key={child.title}
                                    className={`${classes.subsubCat} ${
                                      child.id === filterByCategory
                                        ? classes.categActif
                                        : null
                                    }`}
                                    onClick={() =>
                                      handleFilterCategory(child.id)
                                    }
                                  >
                                    {child.title}
                                  </ListItem>
                                ))}
                              </ul>
                            </Collapse>
                          )}
                        </Fragment>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Fragment>
            ))}
        </List>
      )}
    </div>
  );
}

export default SideMenu;
